import React, { useState, useEffect, useRef } from "react";
import { Typography, styled, keyframes, Box } from "@mui/material";

// ================== SPECIALIZED ANIMATIONS FOR COMMON UI PATTERNS ==================

// 1. Count-up Animation for Numbers
interface CountUpTextProps {
  start?: number;
  end: number;
  duration?: number;
  delay?: number;
  trigger?: boolean;
  prefix?: string;
  suffix?: string;
  component?: React.ElementType;
  sx?: any;
}

export const CountUpText: React.FC<CountUpTextProps> = ({
  start = 0,
  end,
  duration = 2000,
  delay = 0,
  trigger = true,
  prefix = "",
  suffix = "",
  component: Component = Typography,
  sx = {},
}) => {
  const [current, setCurrent] = useState(start);
  const [hasStarted, setHasStarted] = useState(false);

  useEffect(() => {
    if (!trigger || hasStarted) return;

    const timer = setTimeout(() => {
      setHasStarted(true);
      const increment = (end - start) / (duration / 16); // 60fps
      const interval = setInterval(() => {
        setCurrent((prev) => {
          const next = prev + increment;
          if (next >= end) {
            clearInterval(interval);
            return end;
          }
          return next;
        });
      }, 16);

      return () => clearInterval(interval);
    }, delay);

    return () => clearTimeout(timer);
  }, [trigger, start, end, duration, delay, hasStarted]);

  const displayValue = Math.floor(current);

  return (
    <Component sx={sx}>
      {prefix}{displayValue.toLocaleString()}{suffix}
    </Component>
  );
};

// 2. Loading Dots Animation
const loadingDots = keyframes`
  0%, 20% { opacity: 0; }
  50% { opacity: 1; }
  100% { opacity: 0; }
`;

const LoadingDot = styled('span')<{ delay: number }>(({ delay }) => ({
  animation: `${loadingDots} 1.5s ease-in-out ${delay}s infinite`,
}));

interface LoadingTextProps {
  baseText: string;
  dotCount?: number;
  component?: React.ElementType;
  sx?: any;
}

export const LoadingText: React.FC<LoadingTextProps> = ({
  baseText,
  dotCount = 3,
  component: Component = Typography,
  sx = {},
}) => {
  return (
    <Component sx={sx}>
      {baseText}
      {Array.from({ length: dotCount }, (_, i) => (
        <LoadingDot key={i} delay={i * 0.2}>
          .
        </LoadingDot>
      ))}
    </Component>
  );
};

// 3. Text Rotation Animation
interface RotatingTextProps {
  texts: string[];
  interval?: number;
  delay?: number;
  trigger?: boolean;
  component?: React.ElementType;
  sx?: any;
}

const fadeInOut = keyframes`
  0% { opacity: 0; transform: translateY(20px); }
  20%, 80% { opacity: 1; transform: translateY(0); }
  100% { opacity: 0; transform: translateY(-20px); }
`;

const RotatingTextContainer = styled(Box)({
  position: 'relative',
  display: 'inline-block',
  minHeight: '1.2em',
});

const RotatingTextItem = styled('span')<{ isVisible: boolean; duration: number }>(({ isVisible, duration }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  opacity: isVisible ? 1 : 0,
  animation: isVisible ? `${fadeInOut} ${duration}ms ease-in-out` : 'none',
}));

export const RotatingText: React.FC<RotatingTextProps> = ({
  texts,
  interval = 3000,
  delay = 0,
  trigger = true,
  component: Component = Typography,
  sx = {},
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);

  useEffect(() => {
    if (!trigger || texts.length === 0) return;

    const startTimer = setTimeout(() => {
      setHasStarted(true);
      const rotationInterval = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % texts.length);
      }, interval);

      return () => clearInterval(rotationInterval);
    }, delay);

    return () => clearTimeout(startTimer);
  }, [trigger, texts, interval, delay]);

  if (!hasStarted || texts.length === 0) {
    return <Component sx={sx}>{texts[0] || ''}</Component>;
  }

  return (
    <Component sx={sx}>
      <RotatingTextContainer>
        {texts.map((text, index) => (
          <RotatingTextItem
            key={index}
            isVisible={index === currentIndex}
            duration={interval}
          >
            {text}
          </RotatingTextItem>
        ))}
      </RotatingTextContainer>
    </Component>
  );
};

// 4. Text Highlight Animation
const highlightSweep = keyframes`
  0% { width: 0; }
  100% { width: 100%; }
`;

const HighlightContainer = styled('span')({
  position: 'relative',
  display: 'inline-block',
});

const HighlightBackground = styled('span')<{ trigger: boolean; delay: number; color: string }>(({ trigger, delay, color }) => ({
  position: 'absolute',
  bottom: '10%',
  left: 0,
  height: '60%',
  backgroundColor: color,
  opacity: 0.3,
  zIndex: -1,
  width: trigger ? '100%' : '0%',
  animation: trigger ? `${highlightSweep} 1s ease-out ${delay}s both` : 'none',
}));

interface HighlightTextProps {
  children: React.ReactNode;
  highlightColor?: string;
  delay?: number;
  trigger?: boolean;
  component?: React.ElementType;
  sx?: any;
}

export const HighlightText: React.FC<HighlightTextProps> = ({
  children,
  highlightColor = '#29BC62',
  delay = 0,
  trigger = true,
  component: Component = Typography,
  sx = {},
}) => {
  return (
    <Component sx={sx}>
      <HighlightContainer>
        <HighlightBackground
          trigger={trigger}
          delay={delay}
          color={highlightColor}
        />
        {children}
      </HighlightContainer>
    </Component>
  );
};

// 5. Matrix/Code Rain Effect
const matrixRain = keyframes`
  0% { opacity: 1; transform: translateY(-100%); }
  100% { opacity: 0; transform: translateY(100%); }
`;

const MatrixChar = styled('span')<{ delay: number; duration: number }>(({ delay, duration }) => ({
  position: 'absolute',
  color: '#29BC62',
  fontFamily: 'monospace',
  animation: `${matrixRain} ${duration}ms linear ${delay}ms infinite`,
  fontSize: '14px',
}));

interface MatrixTextProps {
  text: string;
  trigger?: boolean;
  component?: React.ElementType;
  sx?: any;
}

export const MatrixText: React.FC<MatrixTextProps> = ({
  text,
  trigger = true,
  component: Component = Typography,
  sx = {},
}) => {
  const [matrixChars, setMatrixChars] = useState<string[]>([]);

  useEffect(() => {
    if (!trigger) return;

    const chars = '01アイウエオカキクケコサシスセソタチツテトナニヌネノハヒフヘホマミムメモヤユヨラリルレロワヲン';
    const newMatrixChars = Array.from({ length: 20 }, () =>
      chars[Math.floor(Math.random() * chars.length)]
    );
    setMatrixChars(newMatrixChars);
  }, [trigger]);

  return (
    <Component sx={{ position: 'relative', overflow: 'hidden', ...sx }}>
      {trigger && matrixChars.map((char, index) => (
        <MatrixChar
          key={index}
          delay={index * 100}
          duration={2000 + Math.random() * 1000}
          style={{
            left: `${Math.random() * 100}%`,
            top: 0,
          }}
        >
          {char}
        </MatrixChar>
      ))}
      <span style={{ position: 'relative', zIndex: 1 }}>{text}</span>
    </Component>
  );
};

// 6. Scramble Text Effect
interface ScrambleTextProps {
  text: string;
  duration?: number;
  delay?: number;
  trigger?: boolean;
  component?: React.ElementType;
  sx?: any;
}

export const ScrambleText: React.FC<ScrambleTextProps> = ({
  text,
  duration = 2000,
  delay = 0,
  trigger = true,
  component: Component = Typography,
  sx = {},
}) => {
  const [displayText, setDisplayText] = useState(text);
  const [isScrambling, setIsScrambling] = useState(false);

  useEffect(() => {
    if (!trigger || isScrambling) return;

    const startTimer = setTimeout(() => {
      setIsScrambling(true);
      const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
      const steps = 20;
      const stepDuration = duration / steps;
      let currentStep = 0;

      const scrambleInterval = setInterval(() => {
        const scrambledText = text
          .split('')
          .map((char, index) => {
            if (char === ' ') return ' ';
            const progress = currentStep / steps;
            const revealPoint = index / text.length;

            if (progress > revealPoint) {
              return char;
            } else {
              return chars[Math.floor(Math.random() * chars.length)];
            }
          })
          .join('');

        setDisplayText(scrambledText);
        currentStep++;

        if (currentStep > steps) {
          clearInterval(scrambleInterval);
          setDisplayText(text);
          setIsScrambling(false);
        }
      }, stepDuration);

      return () => clearInterval(scrambleInterval);
    }, delay);

    return () => clearTimeout(startTimer);
  }, [trigger, text, duration, delay, isScrambling]);

  return (
    <Component sx={{ fontFamily: 'monospace', ...sx }}>
      {displayText}
    </Component>
  );
};

// 7. Neon Glow Text
const neonGlow = keyframes`
  0%, 100% {
    text-shadow:
      0 0 5px #29BC62,
      0 0 10px #29BC62,
      0 0 15px #29BC62,
      0 0 20px #29BC62;
  }
  50% {
    text-shadow:
      0 0 2px #29BC62,
      0 0 5px #29BC62,
      0 0 8px #29BC62,
      0 0 12px #29BC62;
  }
`;

const NeonText = styled(Typography)<{ trigger: boolean; delay: number }>(({ trigger, delay }) => ({
  color: '#29BC62',
  fontWeight: 'bold',
  animation: trigger ? `${neonGlow} 2s ease-in-out ${delay}s infinite` : 'none',
}));

interface NeonGlowTextProps {
  children: React.ReactNode;
  delay?: number;
  trigger?: boolean;
  component?: React.ElementType;
  sx?: any;
}

export const NeonGlowText: React.FC<NeonGlowTextProps> = ({
  children,
  delay = 0,
  trigger = true,
  component = Typography,
  sx = {},
}) => {
  const Component = component;
  return (
    <NeonText
      as={Component}
      trigger={trigger}
      delay={delay}
      sx={sx}
    >
      {children}
    </NeonText>
  );
};

// Export all specialized components
export default {
  CountUpText,
  LoadingText,
  RotatingText,
  HighlightText,
  MatrixText,
  ScrambleText,
  NeonGlowText,
};